/* Lane 2 local PDP runtime. Bundle composition/coordinator intentionally absent. */
const positiveInteger = (value) => Number.isSafeInteger(Number(value)) && Number(value) >= 1;
const emit = (target, name, detail) => target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
const hostFor = (element) => element.closest('[data-ib2-product]');

function cleanupSwatches(root) {
  const prefixes = new Set();
  if (root.dataset.ib2SwatchPrefix) prefixes.add(root.dataset.ib2SwatchPrefix);
  root.querySelectorAll('[data-ib2-swatch-prefix]').forEach((node) => prefixes.add(node.dataset.ib2SwatchPrefix));
  const section = root.dataset.sectionId;
  root.querySelectorAll('[aria-controls]').forEach((node) => {
    const id = node.getAttribute('aria-controls');
    if (section && id?.startsWith(`${section}-`)) prefixes.add(id);
  });
  const owned = () => [...document.body.querySelectorAll('.ib-offer__swatch-dropdown__options')]
    .filter((node) => [...prefixes].some((prefix) => node.id === prefix || node.id.startsWith(`${prefix}-`)));
  if (!owned().length) return;
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  owned().forEach((node) => node.remove());
}

function allowedVariants(root, key = 'ib2AllowedVariants') {
  let ids;
  try { ids = JSON.parse(root.dataset[key]); } catch { return new Set(); }
  if (!Array.isArray(ids) || ids.some((id) => !positiveInteger(id))) return new Set();
  return new Set(ids.map(Number));
}

function purchaseItems(root) {
  const allowed = allowedVariants(root);
  const available = allowedVariants(root, 'ib2AvailableVariants');
  const offer = root.querySelector('[data-ib-offer]');
  let items;
  if (offer) {
    const selected = [...offer.querySelectorAll('.ib-offer__bars > .ib-offer__bar--selected')]
      .filter((bar) => bar.closest('[data-ib-offer]') === offer && !bar.closest('[data-ib-upsell]'));
    if (selected.length !== 1) throw new Error('invalid');
    const tier = selected[0];
    const quantity = Number(tier.dataset.ibQty);
    if (tier.hidden || tier.closest('[hidden]') || tier.getAttribute('aria-disabled') === 'true'
      || tier.querySelector('input:disabled') || !positiveInteger(quantity)
      || !allowed.has(Number(tier.dataset.ibVariantId))) throw new Error('invalid');
    const swatches = tier.querySelectorAll('.ib-offer__swatch-dropdown');
    if (swatches.length && !positiveInteger(quantity / swatches.length)) throw new Error('invalid');
    if (typeof window.ibOffer?.items !== 'function') throw new Error('service');
    items = window.ibOffer.items(offer);
  } else {
    const select = root.querySelector('[data-ib2-variant]');
    const option = select?.selectedOptions?.[0];
    if (!option || option.disabled || option.dataset.available !== 'true') throw new Error('unavailable');
    items = [{ id: Number(select.value), quantity: Number(root.querySelector('[data-ib2-quantity]')?.value) }];
  }
  if (!Array.isArray(items) || !items.length || items.some((item) => !item || !positiveInteger(item.id)
    || !allowed.has(Number(item.id)) || !available.has(Number(item.id)) || !positiveInteger(item.quantity))) throw new Error('invalid');
  return items.map(({ id, quantity, properties }) => ({ id: Number(id), quantity: Number(quantity), ...(properties && { properties }) }));
}

function updateVariantPrice(host, variant) {
  host?.querySelectorAll('[data-ib2-product-price]').forEach((node) => {
    const template = [...node.querySelectorAll('template[data-ib2-price-for]')]
      .find((item) => item.dataset.ib2PriceFor === String(variant));
    if (template) node.querySelector('[data-ib2-price-display]')?.replaceChildren(template.content.cloneNode(true));
  });
  host?.querySelectorAll('.ib2-sticky-atc__image').forEach((node) => {
    const template = [...node.querySelectorAll('template[data-ib2-sticky-image-for]')]
      .find((item) => item.dataset.ib2StickyImageFor === String(variant));
    if (template) node.querySelector('[data-ib2-sticky-image-display]')?.replaceChildren(template.content.cloneNode(true));
  });
}

function updateOfferPrice(root) {
  const offer = root.querySelector('[data-ib-offer]');
  const tier = offer?.querySelector('.ib-offer__bars > .ib-offer__bar--selected');
  if (!tier || tier.dataset.ib2Price === undefined) return;
  hostFor(root)?.querySelectorAll('[data-ib2-product-price]').forEach((node) => {
    const display = node.querySelector('[data-ib2-price-display]');
    if (!display) return;
    const price = document.createElement('span');
    price.className = 'ib2-price';
    const current = document.createElement('span');
    current.setAttribute('data-ib-money', '');
    current.textContent = tier.dataset.ib2Price;
    price.append(current);
    if (node.dataset.ib2ShowCompare === 'true' && tier.dataset.ib2Compare) {
      const compare = document.createElement('s');
      compare.className = 'ib2-price__compare';
      compare.textContent = tier.dataset.ib2Compare;
      price.append(compare);
      const key = node.dataset.ib2Savings === 'amount' ? 'ib2SavingsAmount' : 'ib2SavingsPercent';
      if (node.dataset.ib2Savings !== 'none' && tier.dataset[key]) {
        const savings = document.createElement('span');
        savings.className = 'ib2-price__savings';
        savings.textContent = tier.dataset[key];
        price.append(savings);
      }
    }
    display.replaceChildren(price);
  });
}

class IB2ProductForm extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-ib2-core-atc]');
    this.error = this.querySelector('[data-ib2-product-error]');
    this.form?.addEventListener('submit', (event) => { event.preventDefault(); this.submit(); }, options);
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-ib2-variant]')) this.variantChanged();
    }, options);
    this.addEventListener('ib:offer:change', () => this.variantChanged({ offerDriven: true }), options);
    this.addEventListener('click', (event) => {
      const upsell = event.target.closest('[data-ib-upsell]');
      if (upsell && this.contains(upsell) && this.querySelector('[data-ib2-headline-includes-upsells="true"]')) updateOfferPrice(this);
    }, options);
    this.variantChanged();
  }

  disconnectedCallback() {
    cleanupSwatches(this);
    this.events?.abort();
    this.events = null;
  }

  variantChanged({ offerDriven = false } = {}) {
    const select = this.querySelector('[data-ib2-variant]');
    const input = this.querySelector('[data-ib2-id]');
    if (select && input) {
      input.value = select.value;
      input.disabled = false;
      select.removeAttribute('name');
    }
    const offer = this.querySelector('[data-ib-offer]');
    const selected = offer ? [...offer.querySelectorAll('.ib-offer__bars > .ib-offer__bar--selected')]
      .filter((bar) => bar.closest('[data-ib-offer]') === offer && !bar.closest('[data-ib-upsell]')) : [];
    const available = offer ? selected.length === 1 && !selected[0].hidden && !selected[0].closest('[hidden]')
      && selected[0].getAttribute('aria-disabled') !== 'true' && !selected[0].querySelector('input:disabled')
      : select?.selectedOptions?.[0]?.dataset.available === 'true';
    if (this.button) this.button.disabled = Boolean(this.busy) || !available;
    const label = this.querySelector('[data-ib2-atc-label]');
    if (label) label.textContent = available ? label.dataset.ib2AvailableLabel : label.dataset.ib2SoldLabel;
    if (offer) updateOfferPrice(this);
    else updateVariantPrice(hostFor(this), select?.value);
    const detail = { id: select?.value, available };
    if (offerDriven) detail.offerDriven = true;
    emit(this, 'ib2:variant:change', detail);
    this.syncSticky();
  }

  syncSticky() {
    hostFor(this)?.querySelectorAll('[data-ib2-sticky-submit]').forEach((button) => {
      button.disabled = Boolean(this.button?.disabled);
      button.textContent = this.button?.disabled && !this.busy ? button.dataset.ib2SoldLabel : button.dataset.ib2AvailableLabel;
    });
  }

  async submit() {
    if (this.busy) return;
    if (this.error) { this.error.hidden = true; this.error.textContent = ''; }
    let items;
    try {
      items = purchaseItems(this);
      if (typeof window.ibCart?.add !== 'function') throw new Error('service');
    } catch (error) {
      this.showError(error.message);
      return;
    }
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    if (this.button) this.button.disabled = true;
    this.syncSticky();
    try {
      const result = await window.ibCart.add({ items });
      if (!result?.ok) throw result || new Error('service');
      if (this.isConnected) emit(this, 'ib:cart:open', { items, source: 'ib2-product' });
    } catch (error) {
      this.showError('service');
      emit(this, 'ib:cart:error', { source: 'ib2-product', error });
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.variantChanged();
    }
  }

  showError(kind) {
    if (!this.error) return;
    this.error.textContent = kind === 'unavailable' ? this.dataset.ib2Unavailable
      : kind === 'invalid' ? this.dataset.ib2Invalid : this.dataset.ib2ServiceError;
    this.error.hidden = false;
  }
}

class IB2Gallery extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.track = this.querySelector('[data-ib2-gallery-track]');
    this.dialog = this.querySelector('[data-ib2-gallery-dialog]');
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-ib2-thumbnail]');
      if (thumb) this.show(thumb.dataset.mediaId);
      if (event.target.closest('[data-ib2-gallery-prev]')) this.move(-1);
      if (event.target.closest('[data-ib2-gallery-next]')) this.move(1);
      if (event.target.closest('[data-ib2-gallery-close]')) this.dialog?.close();
      const slide = event.target.closest('[data-ib2-slide]');
      if (slide && this.dataset.ib2Zoom === 'lightbox' && !event.target.closest('ib2-video')) {
        this.zoom(slide);
      }
    }, options);
    this.track?.addEventListener('scroll', () => this.markCurrent(), { ...options, passive: true });
    this.addEventListener('keydown', (event) => {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.move(event.key === 'ArrowLeft' ? -1 : 1); }
      if ((event.key === 'Enter' || event.key === ' ') && this.dataset.ib2Zoom === 'lightbox') {
        event.preventDefault();
        this.zoom(this.current());
      }
    }, options);
    hostFor(this)?.addEventListener('ib2:variant:change', (event) => {
      if (event.detail.offerDriven) return;
      const slide = this.slides().find((node) => (node.dataset.variantIds || '').split(',').includes(String(event.detail.id)));
      if (slide) this.show(slide.dataset.mediaId);
    }, options);
    this.markCurrent();
  }

  disconnectedCallback() { this.events?.abort(); this.events = null; this.dialog?.close(); }
  zoom(slide) {
    if (!slide || slide.querySelector('ib2-video') || !this.dialog?.showModal) return;
    const image = slide.querySelector('img');
    if (!image) return;
    const clone = image.cloneNode(true);
    clone.removeAttribute('id');
    this.querySelector('[data-ib2-gallery-zoom-content]')?.replaceChildren(clone);
    this.dialog.showModal();
  }
  slides() { return [...this.querySelectorAll('[data-ib2-slide]')]; }
  current() {
    const track = this.track;
    if (!track) return null;
    const left = track.getBoundingClientRect().left;
    return this.slides().reduce((nearest, node) => !nearest || Math.abs(node.getBoundingClientRect().left - left)
      < Math.abs(nearest.getBoundingClientRect().left - left) ? node : nearest, null);
  }
  markCurrent() {
    const current = this.current();
    this.querySelectorAll('[data-ib2-thumbnail]').forEach((node) => node.setAttribute('aria-current', String(node.dataset.mediaId === current?.dataset.mediaId)));
  }
  show(id) {
    const slide = this.slides().find((node) => node.dataset.mediaId === String(id));
    if (!slide || !this.track) return;
    this.track.scrollTo({ left: this.track.scrollLeft + slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }
  move(direction) {
    const slides = this.slides();
    const index = slides.indexOf(this.current());
    const target = slides[Math.max(0, Math.min(slides.length - 1, index + direction))];
    if (target) this.show(target.dataset.mediaId);
  }
}

// Sticky bar money (STICKY 16/10): sample = '{{ 123456 | money }}' of this lane, e.g. '€1.234,56' / '$1,234.56' / '1.234,56 €'.
// A format without 2 decimals does not match, so the bar hides its price rather than print a wrong one.
function stickyMoney(cents, sample) {
  const m = /^(.*?)1(\D?)234(\D)56(.*)$/.exec(sample || '');
  if (!m || !Number.isSafeInteger(cents) || cents < 0) return '';
  return m[1] + String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+$)/g, m[2]) + m[3] + String(cents % 100).padStart(2, '0') + m[4];
}

// [price, strike] of the ATC payload (ibOffer.items) from the block's cents map {fmt, v: {id: [price, compare_at]}}; null when an id is unknown.
function stickyTotal(items, prices) {
  if (!Array.isArray(items) || !items.length || !prices?.v) return null;
  let now = 0, was = 0;
  for (const { id, quantity } of items) {
    const p = prices.v[id];
    if (!p || !positiveInteger(quantity)) return null;
    now += p[0] * quantity;
    was += Math.max(p[0], p[1] || 0) * quantity;
  }
  const price = stickyMoney(now, prices.fmt);
  return price ? [price, was > now ? stickyMoney(was, prices.fmt) : ''] : null;
}

// Sticky ATC (STICKY 16/10): <1024 px = show_m, else show_d; shown only once the main ATC has left the viewport upward and the footer is
// not on screen (scroll listener, not IntersectionObserver: a jump past the ATC never crosses a threshold). Cart drawer / cookie banner
// hide it in CSS. Design = data-v (block setting), ?ib_sticky=a|b|c overrides it on unpublished themes only. Tap = the main form submit.
class IB2StickyATC extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    const host = hostFor(this);
    this.core = host?.querySelector('ib2-product-form');
    this.atc = this.core?.querySelector('[data-ib2-core-atc]');
    this.offer = this.core?.querySelector('[data-ib-offer]');
    this.footer = document.querySelector('.shopify-section-group-footer-group, footer');
    try { this.prices = JSON.parse(this.querySelector('[data-ib2-sticky-prices]')?.textContent); } catch { this.prices = null; }
    const pick = new URLSearchParams(location.search).get('ib_sticky');
    if (/^[abc]$/.test(pick || '') && window.Shopify?.theme?.role !== 'main') this.dataset.v = pick;
    this.button = this.querySelector('[data-ib2-sticky-submit]');
    const key = (this.dataset.v || 'a').toUpperCase();
    if (this.button?.dataset[`ib2Label${key}`]) this.button.dataset.ib2AvailableLabel = this.button.dataset[`ib2Label${key}`];
    this.viewport = window.matchMedia('(max-width: 1023.98px)');
    let frame = 0;
    const queue = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; this.visibility(); }); };
    window.addEventListener('scroll', queue, { ...options, passive: true });
    window.addEventListener('resize', queue, options);
    this.viewport.addEventListener('change', () => this.visibility(), options);
    this.addEventListener('ib2:editor-reveal', () => this.visibility(), options);
    this.addEventListener('ib2:editor-conceal', () => this.visibility(), options);
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-ib2-sticky-submit]')) this.core?.form?.requestSubmit();
    }, options);
    // tab change (ib2:variant:change), add-on toggle (no event: ib-offer.js toggles in its capture listener, this bubble one runs after),
    // [data-ib-once] bag hidden on ib:cart:updated (ib-offer.js listener may run after this one, hence the task)
    host?.addEventListener('ib2:variant:change', () => this.refresh(), options);
    host?.addEventListener('click', () => this.refresh(), options);
    document.addEventListener('ib:cart:updated', () => setTimeout(() => this.refresh()), options);
    this.core?.syncSticky?.();
    this.refresh();
    this.visibility();
  }
  refresh() {
    const tier = this.offer?.querySelector('.ib-offer__bars > .ib-offer__bar--selected');
    const name = this.querySelector('[data-ib2-sticky-name]');
    if (name) name.textContent = tier?.querySelector('.ibr__tn')?.textContent.trim() || '';
    const thumb = this.querySelector('[data-ib2-sticky-thumb]');
    const source = tier?.querySelector('.ibr__th img');
    if (thumb) {
      if (source?.getAttribute('srcset')) thumb.setAttribute('srcset', source.getAttribute('srcset'));
      if (source) thumb.src = source.src;
      thumb.parentElement.hidden = !source;
    }
    const ear = this.querySelector('[data-ib2-sticky-ear]');
    const save = tier?.querySelector('.ibb__save, .ibr__off');
    if (ear) {
      ear.textContent = save?.textContent.trim() || '';
      ear.hidden = !ear.textContent;
      ear.style.setProperty('--g', save?.style.getPropertyValue('--ibb-g') || '');
    }
    // body tab with no add-on ticked: the tab's own strings, exactly what updateOfferPrice prints in the main ATC (lock 7A);
    // otherwise (face tab + Bag/serum) the sum of the ATC payload
    const addon = this.offer?.querySelector('[data-ib-upsell].ib-offer__bar--selected:not([hidden])');
    const total = tier?.dataset.ib2Price !== undefined && !addon ? [tier.dataset.ib2Price, tier.dataset.ib2Compare || '']
      : this.offer ? stickyTotal(window.ibOffer?.items?.(this.offer), this.prices) : null;
    const box = this.querySelector('[data-ib2-sticky-price]');
    if (box) {
      box.hidden = !total?.[0];
      box.querySelector('b').textContent = total?.[0] || '';
      const strike = box.querySelector('s');
      strike.textContent = total?.[1] || '';
      strike.hidden = !total?.[1];
    }
    if (this.button) {
      const aria = this.button.dataset[`ib2Aria${(this.dataset.v || 'a').toUpperCase()}`];
      if (aria && !(this.core?.button?.disabled && !this.core?.busy)) this.button.setAttribute('aria-label', aria);
      else this.button.removeAttribute('aria-label');
    }
  }
  visibility() {
    if (this.dataset.ib2EditorSelected !== undefined) { this.hidden = false; this.toggleAttribute('data-on', true); return; }
    const enabled = this.viewport.matches ? this.dataset.showMobile === 'true' : this.dataset.showDesktop === 'true';
    this.hidden = !(enabled && this.atc && this.core?.isConnected);
    if (this.hidden) { this.removeAttribute('data-on'); return; }
    const passed = this.atc.getBoundingClientRect().bottom < 0;
    const footer = this.footer ? this.footer.getBoundingClientRect().top < window.innerHeight : false;
    const on = passed && !footer;
    // re-read on every slide-in: the offer state may have settled after connect (ib-offer.js load order, pre-ticked bag)
    if (on && !this.hasAttribute('data-on')) this.refresh();
    this.toggleAttribute('data-on', on);
  }
  disconnectedCallback() { this.events?.abort(); this.events = null; this.hidden = this.dataset.ib2EditorSelected === undefined; }
}

if (!customElements.get('ib2-product-form')) customElements.define('ib2-product-form', IB2ProductForm);
if (!customElements.get('ib2-gallery')) customElements.define('ib2-gallery', IB2Gallery);
if (!customElements.get('ib2-sticky-atc')) customElements.define('ib2-sticky-atc', IB2StickyATC);

window.addEventListener('click', (event) => {
  const bar = event.target.closest?.('.ib-offer__bar[aria-disabled="true"]');
  if (bar?.closest('ib2-product-form')) { event.preventDefault(); event.stopImmediatePropagation(); }
}, { capture: true });
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('ib2-product-form').forEach(cleanupSwatches);
});
